/**
 * The one place that knows how JAMS product settings are shaped, merged, and saved.
 *
 * Before this module, every settings control built its own payload by spreading
 * whatever the store happened to hold and re-listing the sibling keys it did not
 * own. Three copies of that had already drifted, and each new setting added a
 * fourth. Everything here exists so a control can say only what it changed.
 *
 * Two details about the JAMS contract are worth stating, because they are the
 * reason a shared merge is needed rather than a plain object spread:
 *
 * - A read returns the settings wrapped as `{ config: { ...keys } }`, while a
 *   write expects the inner object and wraps it again. Spreading a read value
 *   straight into a write therefore nests one `config` deeper every time, and
 *   reading a key off the unwrapped value always misses. `normalizeProductSettings`
 *   collapses both shapes to the flat object the rest of the app expects.
 * - Settings are shared by every feature of the product, so a write must preserve
 *   keys this app does not know about. Merging is always additive over the
 *   current value; nothing is dropped because it was unrecognized.
 */
import { updateProductSettings } from './apis'
import {
  DEFAULT_DESCRIPTION_LANGUAGE,
  DEFAULT_HIGHLIGHT_LANGUAGE_SETTING,
  normalizeHighlightLanguageSetting,
  normalizeLanguage,
} from './contentLanguage'
import type { HighlightLanguageSetting, ResolvedContentLanguage } from './contentLanguage'

export type ProductSettings = Record<string, unknown>

/** A change to one or more settings. Only the keys a control owns. */
export type ProductSettingsPatch = ProductSettings

const isPlainObject = (value: unknown): value is ProductSettings =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Collapse a settings value to the flat object of keys.
 *
 * Accepts either the wrapped read shape or an already-flat object, because both
 * reach the store: the app container commits the raw read, while a successful
 * save commits the flat merged value.
 *
 * A `config` never survives into the result, which is what makes this safe to
 * apply repeatedly. It also repairs accounts whose stored settings picked up a
 * nested `config` from an earlier save that spread the read shape into a write:
 * the buried keys are recovered rather than dropped, and the keys written more
 * recently, at the shallower level, win.
 */
export const normalizeProductSettings = (raw: unknown): ProductSettings => {
  if (!isPlainObject(raw)) return {}

  const { config, ...settings } = raw
  return isPlainObject(config) ? { ...normalizeProductSettings(config), ...settings } : settings
}

/**
 * Apply a patch over the current settings.
 *
 * Returns a new flat object; the input is never mutated, so a failed save
 * leaves the store exactly as it was.
 */
export const mergeProductSettings = (current: unknown, patch: ProductSettingsPatch): ProductSettings => ({
  ...normalizeProductSettings(current),
  ...patch,
})

// ---------------------------------------------------------------------------
// Content language
// ---------------------------------------------------------------------------

export const DESCRIPTION_LANGUAGE_KEY = 'descriptionLanguage'
export const HIGHLIGHT_LANGUAGE_KEY = 'highlightLanguage'

export interface StoredLanguageSettings {
  descriptionLanguage: ResolvedContentLanguage
  /** May be `same-as-description`; that choice is preserved, not collapsed. */
  highlightLanguage: HighlightLanguageSetting
}

/**
 * Read the language settings as the user chose them.
 *
 * Distinct from `resolveTargetLanguages`, which answers what language content
 * will actually be in. A control has to show the stored choice, so
 * `same-as-description` must survive being read back.
 */
export const readLanguageSettings = (raw: unknown): StoredLanguageSettings => {
  const settings = normalizeProductSettings(raw)
  const description = normalizeLanguage(settings[DESCRIPTION_LANGUAGE_KEY])

  return {
    descriptionLanguage: description === 'unknown' ? DEFAULT_DESCRIPTION_LANGUAGE : description,
    highlightLanguage: normalizeHighlightLanguageSetting(settings[HIGHLIGHT_LANGUAGE_KEY]),
  }
}

/**
 * Build a unified content-language patch.
 *
 * The editable form owns one canonical language. Persistence still uses the
 * existing two-field contract, so both keys are written explicitly and never as
 * `same-as-description`. Unrecognized values fall back to English.
 */
export const buildContentLanguagePatch = (value: unknown): ProductSettingsPatch => {
  const normalized = normalizeLanguage(value)
  const language = normalized === 'unknown' ? DEFAULT_DESCRIPTION_LANGUAGE : normalized
  return {
    [DESCRIPTION_LANGUAGE_KEY]: language,
    [HIGHLIGHT_LANGUAGE_KEY]: language,
  }
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/** What a save needs to know about who is asking. Resolved by the caller. */
export interface ProductSettingsContext {
  accountId: string
  productId: string
  isClientUser: boolean
  sessionToken: string
  current: unknown
}

export interface SaveResult {
  saved: boolean
  /** The value to commit to the store on success. */
  settings: ProductSettings
}

/**
 * Merge a patch and persist it.
 *
 * The merged value is returned rather than committed here, so the store update
 * stays with the caller that owns it and a failed write cannot leave the UI
 * showing a setting the server rejected.
 */
export const saveProductSettings = async (
  context: ProductSettingsContext,
  patch: ProductSettingsPatch,
): Promise<SaveResult> => {
  const settings = mergeProductSettings(context.current, patch)

  const saved = await updateProductSettings(
    context.accountId,
    context.productId,
    settings,
    !context.isClientUser,
    context.sessionToken,
  )

  return { saved, settings }
}

export { DEFAULT_DESCRIPTION_LANGUAGE, DEFAULT_HIGHLIGHT_LANGUAGE_SETTING }
